/**
 * Lob haircuts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_named_haircuts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const lobHaircutsPack: ToilePack = {
    slug: 'lob-haircuts',
    category: 'beauty',
    name: 'Lob haircuts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'blunt', label: 'Blunt lob', subject: 'a blunt straight lob at the collarbone with a centre part'},
        {key: 'textured', label: 'Textured lob', subject: 'a beachy textured lob with soft waves'},
        {key: 'angled', label: 'Angled lob', subject: 'an angled lob shorter at the back and longer at the front'},
    ],
    styles: [
        {key: 'salon', label: 'Salon', style: 'photographed in a bright hair salon by soft broad window daylight from camera-left, a light and open balance with few dark tones', palette: 'warm white walls and pale wood, the hair the darkest tone', framing: 'three-quarter view at eye level, the salon mirror soft behind'},
        {key: 'street', label: 'Street', style: 'photographed outdoors on a city street in soft overcast light from above, an even balance with gentle shadow under the chin', palette: 'pale stone and muted greens', framing: 'eye-level head-and-shoulders, shallow depth of field, the street soft behind'},
        {key: 'editorial', label: 'Editorial', style: 'a beauty editorial portrait against a painted plaster wall, soft directional light from camera-right modelling the face, a mid-tone balance with the far cheek in gentle shadow', palette: 'warm sand plaster, the hair in clear contrast', framing: 'near-frontal head-and-shoulders, centred with space above the crown'},
    ],
    directions: ['grey hair', 'with bangs', 'over 50'],
    directionHint: 'try: grey hair',
    seo: {
        title: 'Lob Haircuts: Blunt, Textured, Angled',
        metaDescription: 'Nine pictures of the lob, a long bob between chin and shoulders: blunt, textured and angled versions in a salon, on a street and in editorial.',
        h1: 'Lob haircuts: blunt, textured, angled',
        tagline: 'Nine pictures, three lobs, each in a salon, on a city street and in an editorial portrait.',
        intro: 'A lob is a long bob that sits between the chin and the shoulders. These pictures show a blunt lob at the collarbone with a centre part, a beachy textured lob with soft waves, and an angled lob shorter at the back and longer at the front. Each is seen in a salon, on a city street and in a beauty editorial portrait. You can type a change, such as grey hair, to ask for the cut with it.',
        useCases: [
            'a reference for length between chin and shoulders',
            'choosing a blunt, textured or angled lob',
            'a wall print',
            'a profile picture',
        ],
        faq: [
            {q: 'What is the difference between the three lobs?', a: 'The blunt lob is straight and sits at the collarbone. The textured lob is beachy with soft waves. The angled lob is shorter at the back and longer at the front.'},
            {q: 'How long is a lob?', a: 'It sits between the chin and the shoulders. The blunt version is shown at the collarbone.'},
            {q: 'Can I change the colour?', a: 'Yes. Type the change and the picture is made with it. It is a request, so the result may differ from the sample.'},
        ],
    },
}
